// Inline click-to-edit title for transcripts (the user-facing noun;
// schema stays "meeting"). Designed for the sticky MeetingReview header
// in Slice 1 and re-used by Slice 3's list-page per-row rename.
//
// Interaction: click the display text -> input appears at the same
// visual weight; Enter saves; Escape cancels; blur saves. Optimistic
// update on the parent via onChange before the network write so the new
// title is on-screen instantly; on save failure we roll back and surface
// the error to the parent.
//
// Empty-title fallback: shows "Untitled transcript YYYY-MM-DD" using the
// recorded_at / created_at date the parent passes through. Plain
// language per the UI-copy convention (no codenames; the schema-level
// "meeting" stays internal).
//
// Persistence:
//   - UPDATE meetings.title via supabase-js (RLS-gated; member can write
//     their own workspace's rows).
//   - INSERT one meeting_events row via lib/events.logUserEvent with
//     payload { old_title, new_title } so Slice 5's Activity panel can
//     render "Renamed transcript from X to Y" and Slice 2's Undo can
//     reverse the change. The event write is best-effort: if it fails,
//     the title change still stands and we log a warning. (Audit gap is
//     recoverable from server logs; the action succeeded.)
//
// See docs/superpowers/plans/2026-05-20-usability-pivot-roadmap.md
// Slice 1 Component 3 + DECISIONS.md 2026-05-20 "Usability-pivot
// roadmap" decision 7 (title-edit inline + per-row).

import { useEffect, useRef, useState } from "react";
import { logUserEvent } from "../lib/events";
import { supabase } from "../lib/supabase";

interface EditableTitleProps {
  meetingId: string;
  workspaceId: string;
  // Current persisted title; null/empty triggers the "Untitled
  // transcript YYYY-MM-DD" fallback at display.
  value: string | null;
  // Date the fallback derives from. Prefer recorded_at; fall back to
  // created_at upstream. ISO-8601 string; we slice the YYYY-MM-DD prefix.
  fallbackDate: string | null;
  // Optimistic-update callback. EditableTitle calls this with the new
  // value (string or null) BEFORE the network write fires. On write
  // failure we call it again with the previous value to roll back.
  onChange: (next: string | null) => void;
  // Optional disable -- e.g., if the parent wants to prevent rename
  // while another mutation is in flight. Defaults to enabled.
  disabled?: boolean;
  // Optional class / inline style passthrough for the display element.
  className?: string;
  style?: React.CSSProperties;
}

function fallbackTitle(isoDate: string | null): string {
  // YYYY-MM-DD slice. If no date is available, omit the date -- showing
  // a wrong date is worse than no date.
  if (!isoDate) return "Untitled transcript";
  const ymd = isoDate.slice(0, 10);
  return `Untitled transcript ${ymd}`;
}

export function EditableTitle({
  meetingId,
  workspaceId,
  value,
  fallbackDate,
  onChange,
  disabled,
  className,
  style,
}: EditableTitleProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<string>(value ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);

  // Grow the textarea's height to fit the wrapped title so the FULL title is
  // visible while renaming (a long title wraps onto more lines instead of
  // scrolling inside a one-line box). The sticky header measures its own
  // height and the audio player below follows (MeetingReview ResizeObserver).
  const autoSize = () => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  };

  // Sync the draft to the source-of-truth `value` if the parent updates
  // it from realtime / refetch while we're not editing. Don't clobber an
  // in-progress edit.
  useEffect(() => {
    if (!editing) setDraft(value ?? "");
  }, [value, editing]);

  // Auto-focus + select-all when entering edit mode, and size to fit.
  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
      autoSize();
    }
  }, [editing]);

  const enterEdit = () => {
    if (disabled || saving) return;
    setError(null);
    setDraft(value ?? "");
    setEditing(true);
  };

  const cancelEdit = () => {
    setDraft(value ?? "");
    setEditing(false);
    setError(null);
  };

  const commit = async () => {
    const trimmed = draft.trim();
    const next: string | null = trimmed.length === 0 ? null : trimmed;
    const prev = value;
    setEditing(false);

    if (next === prev) {
      // No-op rename -- swallow silently, no event row, no DB write.
      return;
    }

    setSaving(true);
    setError(null);
    // Optimistic update so the new title shows up instantly.
    onChange(next);

    try {
      const { error: updateErr } = await supabase
        .from("meetings")
        .update({ title: next })
        .eq("id", meetingId);
      if (updateErr) throw updateErr;

      // Best-effort event write. A failed audit log should NOT roll
      // back the title change -- the title is the user's intent; the
      // event is the audit trail.
      try {
        await logUserEvent({
          meetingId,
          workspaceId,
          eventType: "title_change",
          payload: { old_title: prev, new_title: next },
        });
      } catch (eventErr) {
        // eslint-disable-next-line no-console
        console.warn("title_change event write failed", eventErr);
      }
    } catch (e) {
      // Roll back the optimistic update.
      onChange(prev);
      const msg = e instanceof Error ? e.message : String(e);
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  const displayText = value && value.trim().length > 0
    ? value
    : fallbackTitle(fallbackDate);
  // Pale rendering for the fallback so the user sees "this needs a name"
  // without being shouted at.
  const isFallback = !(value && value.trim().length > 0);

  if (editing) {
    return (
      <span style={{ display: "flex", alignItems: "flex-start", gap: 8, width: "100%", minWidth: 0 }}>
        <textarea
          ref={inputRef}
          rows={1}
          value={draft}
          onChange={(e) => {
            setDraft(e.target.value);
            autoSize();
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              // Enter saves (a title is single-value; no newline inserted).
              e.preventDefault();
              void commit();
            } else if (e.key === "Escape") {
              e.preventDefault();
              cancelEdit();
            }
          }}
          onBlur={() => {
            void commit();
          }}
          disabled={saving}
          placeholder={fallbackTitle(fallbackDate)}
          className={className}
          style={{
            font: "inherit",
            fontWeight: "inherit",
            lineHeight: 1.3,
            // Match the heading visual weight without an extra wrapper.
            background: "var(--bg-input, transparent)",
            border: "1px solid var(--border)",
            borderRadius: 4,
            padding: "2px 6px",
            // Fill the available header width (the space left of the action
            // buttons); a long title WRAPS onto more lines (auto-sized height)
            // so the whole title is visible while renaming, rather than
            // scrolling inside a one-line box or overflowing the buttons.
            flex: 1,
            minWidth: 0,
            width: "100%",
            boxSizing: "border-box",
            resize: "none",
            overflow: "hidden",
            ...style,
          }}
          aria-label="Transcript title"
        />
        {saving && <span style={{ opacity: 0.6, fontSize: 14 }}>Saving…</span>}
        {error && (
          <span role="alert" style={{ color: "var(--danger, #dc2626)", fontSize: 14 }}>
            {error}
          </span>
        )}
      </span>
    );
  }

  return (
    <span
      role="button"
      tabIndex={disabled ? -1 : 0}
      onClick={enterEdit}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          enterEdit();
        }
      }}
      className={className}
      style={{
        cursor: disabled ? "default" : "text",
        opacity: isFallback ? 0.6 : 1,
        // Subtle hover affordance via underline only on enabled state.
        textDecoration: disabled ? "none" : undefined,
        ...style,
      }}
      title={disabled ? undefined : "Click to rename"}
      aria-label={`Transcript title: ${displayText}. Click to rename.`}
    >
      {displayText}
    </span>
  );
}
